import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase-client';
import ErrorMessage from './formComponents/ErrorMessage';
import TextInput from './formComponents/TextInput';
import EmailInput from './formComponents/EmailInput';
import PasswordInput from './formComponents/PasswordInput';
import { PASSWORD_REQUIREMENTS_TEXT, passwordMeetsRequirements } from '../../utils/passwordValidation';
export default function CreateChildAccountForm({
  family,
  onCreated,
  onCancel
}) {
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [enableEmailLogin, setEnableEmailLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const passwordValid = passwordMeetsRequirements(password);
  const passwordsMatch = password === confirmPassword;
  const canSubmit = Boolean(
    username.trim()
    && (!enableEmailLogin || (email.trim() && passwordValid && passwordsMatch))
  );
  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setInfo('');
    const normalizedUsername = username.trim().toLowerCase();
    if (!normalizedUsername) {
      setError('Username is required.');
      return;
    }
    if (enableEmailLogin) {
      if (!email.trim()) {
        setError('Email is required when email login is enabled.');
        return;
      }
      if (!passwordMeetsRequirements(password)) {
        setError(PASSWORD_REQUIREMENTS_TEXT);
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }
    setLoading(true);
    try {
      const fn = httpsCallable(functions, 'createChildAccount');
      const result = await fn({
        familyId: family.id,
        username: normalizedUsername,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dob,
        enableEmailLogin,
        email: email.trim(),
        password,
        contactEmail: contactEmail.trim()
      });
      setInfo('Child account created.');
      onCreated?.(result?.data || {});
    } catch (err) {
      setError(err.message || 'Failed to create child account.');
    } finally {
      setLoading(false);
    }
  };
  return <form onSubmit={handleSubmit} className="flex-column" style={{
    gap: '1rem',
    maxWidth: 500
  }} autoComplete="on">
      <TextInput label="Username" id="child-username" name="username" autoCapitalize="none" spellCheck={false} value={username} onChange={e => setUsername(e.target.value)} required />
      <TextInput label="First Name" id="child-first-name" name="given-name" autoComplete="given-name" value={firstName} onChange={e => setFirstName(e.target.value)} />
      <TextInput label="Last Name" id="child-last-name" name="family-name" autoComplete="family-name" value={lastName} onChange={e => setLastName(e.target.value)} />

      <label style={{
      fontWeight: 600
    }}>
        Date of Birth
        <input type="date" className="form-input" value={dob} onChange={e => setDob(e.target.value)} />
      </label>

      <label style={{
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'flex-start'
    }}>
        <input type="checkbox" checked={enableEmailLogin} onChange={e => setEnableEmailLogin(e.target.checked)} style={{
        marginTop: '0.25rem'
      }} />
        <span>Enable child email login now</span>
      </label>

      {enableEmailLogin ? <>
          <EmailInput
            inputId="child-email"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <PasswordInput
            inputId="child-password"
            name="newPassword"
            value={password}
            onChange={e => setPassword(e.target.value)}
            confirmInputId="child-confirm-password"
            confirmName="confirmPassword"
            confirmValue={confirmPassword}
            onConfirmChange={e => setConfirmPassword(e.target.value)}
            showRequirements
          />
        </> : <label style={{
      fontWeight: 600
    }}>
          Parent Contact Email (optional)
          <input type="email" className="form-input" name="contactEmail" autoComplete="email" autoCapitalize="none" spellCheck={false} inputMode="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
        </label>}

      <div style={{
      display: 'flex',
      gap: '0.5rem'
    }}>
        <button className="button" type="submit" disabled={loading || !canSubmit}>
          {loading ? 'Creating...' : 'Create Child Account'}
        </button>
        <button className="button button-secondary" type="button" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
      </div>

      {error && <ErrorMessage message={error} />}
      {info && <p style={{
      color: 'green',
      margin: 0
    }}>{info}</p>}
    </form>;
}
